import * as React from 'react';
import { dataSources as stardogDataSources } from 'stardog';
import { v4 as uuid } from 'uuid';
import { isEqual, noop } from 'vet-bones/utils';

import { connectionSelector } from 'src/selectors/connection';
import {
  ProjectVirtualGraphField,
  ProjectVirtualGraphOptions,
} from 'src/store/state/projects';
import { addLimitToSqlQuery } from 'src/utils/addLimitToSqlQuery';
import { getStardogConnection } from 'src/utils/getStardogConnection';
import { useDidValueChange } from 'src/utils/hooks/useDidValueChange';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';

const sqlResultsObjectToArray = <T = any>(results: Record<string, T>) => {
  const indexes = Object.keys(results).map((key) => Number.parseInt(key));
  if (indexes.length === 0) {
    return [];
  }
  // The objects we get back from Stardog omit NULL values, along with
  // their keys, so we need to create an array with potentially empty slots
  const arrayResult = new Array(Math.max(...indexes));
  indexes.forEach((key) => {
    arrayResult[key] = results[key];
  });
  return arrayResult;
};

type DataSourceQueryResults = {
  mColumns: Record<
    string,
    {
      mName: string;
      mType: string;
      mSize: number;
    }
  >;
  mRows: Record<string, Record<string, string>>;
};

export type RunningQuery = {
  abortQuery: React.MutableRefObject<() => void>;
  allResultsIncluded: boolean;
  errors: Array<{ message: string }>;
  fields: ProjectVirtualGraphField[];
  isQuerying: boolean;
  rows: string[][];
  updateFieldLabel: (value: string, label: string) => void;
};

export const useProjectDataSourceQuery = ({
  dataSourceId,
  existingFields,
  forceRefresh,
  isCurrentConnectionIncorrect,
  query,
  virtualGraphOptions,
}: {
  dataSourceId?: string;
  existingFields?: ProjectVirtualGraphField[];
  forceRefresh?: boolean;
  isCurrentConnectionIncorrect?: boolean;
  query?: string;
  virtualGraphOptions?: ProjectVirtualGraphOptions;
}): RunningQuery => {
  const connection = useReduxSelector(connectionSelector);
  const [errors, setErrors] = React.useState<Array<{ message: string }>>([]);
  const [fields, setFields] = React.useState<ProjectVirtualGraphField[]>(
    existingFields || []
  );
  const [rows, setRows] = React.useState<string[][]>([]);
  const [allResultsIncluded, setAllResultsIncluded] = React.useState(true);

  const queryIds = React.useRef<Set<string>>(new Set());
  const [isQuerying, setIsQuerying] = React.useState(false);

  const didDataSourceIdChange = useDidValueChange(dataSourceId);
  const didQueryChange = useDidValueChange(query);
  const didConnectionChange = useDidValueChange(connection, isEqual);
  const didExistingFieldsChange = useDidValueChange(existingFields, isEqual);
  const needsRefresh =
    forceRefresh ||
    (!isCurrentConnectionIncorrect &&
      (didDataSourceIdChange ||
        didQueryChange ||
        didConnectionChange ||
        didExistingFieldsChange));

  const abortQuery = React.useRef<() => void>(() => {
    queryIds.current.clear();
    setErrors([]);
    setFields([]);
    setRows([]);
    setAllResultsIncluded(true);
    setIsQuerying(false);
  });

  React.useEffect(() => {
    if (!needsRefresh) {
      return noop;
    }

    const id = uuid();
    const cleanup = () => {
      queryIds.current.delete(id);
      setIsQuerying(Boolean(queryIds.current.size));
    };

    if (dataSourceId && query && connection) {
      queryIds.current.add(id);
      setIsQuerying(true);
      const { limitedQuery, resultLimit } = addLimitToSqlQuery(query);
      const queryWithOptions = virtualGraphOptions
        ? { query: limitedQuery, options: virtualGraphOptions }
        : limitedQuery;
      stardogDataSources
        .query(getStardogConnection(connection), dataSourceId, queryWithOptions)
        .then((response) => {
          if (queryIds.current.has(id) && !response.ok) {
            const errorMessage =
              typeof response.body === 'string'
                ? response.body
                : response.body?.message ||
                  response.statusText ||
                  String(response.status);
            throw new Error(
              `${errorMessage} (${response.body?.code || response.status})`
            );
          }

          const results = response.body as DataSourceQueryResults;
          const newFields = sqlResultsObjectToArray(results.mColumns).map(
            ({ mName }: { mName: string }) => ({
              value: mName,
              label: mName,
            })
          );
          const newRows = sqlResultsObjectToArray(results.mRows).map((row) =>
            sqlResultsObjectToArray(row)
          );

          if (queryIds.current.has(id)) {
            setErrors([]);
            setFields(
              newFields.map((newField, index) => ({
                ...newField,
                label:
                  existingFields?.[index]?.value === newField.value
                    ? existingFields[index].label
                    : newField.label,
              }))
            );
            setRows(newRows);
            setAllResultsIncluded(newRows.length < resultLimit);
          }
          cleanup();
        })
        .catch((err: Error) => {
          setErrors([
            {
              message: `Problem querying data source:\n${err.message}`,
            },
          ]);
          setFields([]);
          setRows([]);
          setAllResultsIncluded(true);
          cleanup();
        });
    } else {
      setErrors([]);
      setFields([]);
      setRows([]);
      setAllResultsIncluded(true);
      cleanup();
    }

    return () => {
      cleanup();
    };
  }, [
    connection,
    dataSourceId,
    existingFields,
    needsRefresh,
    query,
    virtualGraphOptions,
  ]);

  const updateFieldLabel = React.useCallback(
    (value: string, label: string) => {
      setFields(
        fields.map((field) => ({
          ...field,
          label: field.value === value ? label : field.label,
        }))
      );
    },
    [fields]
  );

  return {
    abortQuery,
    allResultsIncluded,
    errors,
    fields,
    isQuerying,
    rows,
    updateFieldLabel,
  };
};
